/**
 * Master Inventory page — companywide list of Equipment + Electrical items.
 * Switchable kind tabs, search, add/edit/delete, and inline test-tag bumping
 * for electrical items.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Zap, Anchor, Plus, Trash2, Edit2, AlertTriangle } from "lucide-react";
import { Button, Card, Badge, Spinner, Input, Modal, Select } from "../shared/ui";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dueBadge = (due) => {
  if (!due || due.state === "unknown") return null;
  if (due.state === "expired") return <Badge variant="danger">OVERDUE</Badge>;
  if (due.state === "warning") return <Badge variant="warning">DUE SOON</Badge>;
  return <Badge variant="success">OK</Badge>;
};

const InventoryPage = () => {
  const [kind, setKind] = useState("equipment");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/${kind}`, { params: search ? { q: search } : {} });
      setItems(r.data.items);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/inventory/${kind}/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/inventory/${kind}`, form);
        toast.success("Added");
      }
      setShowAdd(false);
      setEditing(null);
      setForm({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item from inventory? Job registers referencing it will keep their copy.")) return;
    await axios.delete(`${API}/inventory/${kind}/${id}`);
    toast.success("Deleted");
    load();
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(item);
    setShowAdd(true);
  };

  const isTested = kind === "electrical" || kind === "lifting";
  const testLabel = kind === "lifting" ? "Last Test" : "Last Tag";

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold uppercase">Master Inventory</h1>
        <Button onClick={() => { setEditing(null); setForm({}); setShowAdd(true); }} data-testid="inventory-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
      </div>

      {/* Kind tabs */}
      <div className="flex gap-2 border-b-2 border-slate-200">
        {[
          { id: "equipment", label: "Equipment", icon: Box, color: "text-green-600" },
          { id: "electrical", label: "Electrical", icon: Zap, color: "text-yellow-500" },
          { id: "lifting", label: "Lifting", icon: Anchor, color: "text-purple-600" },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => { setKind(t.id); setSearch(""); }}
              className={`flex items-center gap-2 px-4 py-2 font-bold uppercase text-sm border-b-2 ${kind === t.id ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`inventory-tab-${t.id}`}
            >
              <Icon className={`w-4 h-4 ${kind === t.id ? "text-orange-500" : t.color}`} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <Input placeholder="Search by name or serial..." value={search} onChange={(e) => setSearch(e.target.value)} testId="inventory-search" />
        <Button variant="outline" onClick={load}>Search</Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">No items yet. Click <strong>Add Item</strong> to start your inventory.</Card>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Serial #</th>
                <th className="text-left px-3 py-2">Category</th>
                {isTested && <>
                  <th className="text-left px-3 py-2">{testLabel}</th>
                  <th className="text-left px-3 py-2">Next Due</th>
                  <th className="text-left px-3 py-2">Status</th>
                </>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t hover:bg-slate-50" data-testid={`inventory-row-${i.id}`}>
                  <td className="px-3 py-2 font-bold">{i.name}</td>
                  <td className="px-3 py-2 text-slate-600">{i.serial_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{i.category || "—"}</td>
                  {isTested && <>
                    <td className="px-3 py-2 text-slate-600">{i.last_tag_test_date || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{i.next_tag_test_date || "—"}</td>
                    <td className="px-3 py-2">{dueBadge(i.due)}</td>
                  </>}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(i)} className="text-blue-600 hover:text-blue-800 p-1 mr-1" data-testid={`inventory-edit-${i.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(i.id)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`inventory-del-${i.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Add / Edit modal */}
      {showAdd && (
        <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title={editing ? "Edit Item" : "Add Item"}>
          <div className="space-y-3">
            <Input label="Name *" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="Serial Number" value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            <Input label="Category" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={kind === "electrical" ? "power_board, extension_lead..." : kind === "lifting" ? "shackle, chain_sling, lifting_strap..." : "harness, hand_tool..."} />
            {isTested && (
              <>
                <Input label={kind === "lifting" ? "Last Test Date" : "Last Tag Test Date"} type="date" value={form.last_tag_test_date || ""} onChange={(e) => setForm({ ...form, last_tag_test_date: e.target.value })} />
                <p className="text-xs text-slate-500">Next due will auto-calculate at +3 months on save.</p>
              </>
            )}
            <Input label="Notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} data-testid="inventory-save-btn">Save</Button>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InventoryPage;
